import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaGlobeAmericas, FaExchangeAlt, FaChartLine, FaShieldAlt } from 'react-icons/fa';

const InternationalMarketsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Industries', path: '/industries' },
    { label: 'International Markets' }
  ];

  const services = [
    {
      icon: <FaGlobeAmericas className="w-8 h-8 text-secondary" />,
      title: "Global Investment Strategies",
      description: "Tailored investment strategies that leverage opportunities across international markets, with a focus on emerging economies and growth sectors."
    },
    {
      icon: <FaExchangeAlt className="w-8 h-8 text-secondary" />,
      title: "Cross-Border Transactions",
      description: "Expert guidance for executing complex cross-border mergers, acquisitions, and joint ventures with local market knowledge."
    },
    {
      icon: <FaChartLine className="w-8 h-8 text-secondary" />,
      title: "Currency & Risk Management",
      description: "Sophisticated solutions to manage currency exposure, political risk, and other challenges associated with international investments."
    },
    {
      icon: <FaShieldAlt className="w-8 h-8 text-secondary" />,
      title: "Regulatory Compliance",
      description: "Comprehensive support for navigating the complex regulatory frameworks of multiple jurisdictions to ensure seamless operations."
    }
  ];

  const clientTestimonials = [
    {
      quote: "Segway Capital's global market expertise was invaluable for our expansion into Asian markets. Their local insights and strategic guidance helped us navigate complex regulatory environments with confidence.",
      author: "Jonathan Hayes",
      position: "CEO, Atlas Global Ventures"
    },
    {
      quote: "When we needed to hedge currency risks across our international operations, Segway Capital designed a sophisticated strategy that protected our margins and provided stability in volatile markets.",
      author: "Maria Sanchez",
      position: "CFO, Hemisphere Trading Company"
    },
    {
      quote: "The cross-border acquisition support provided by Segway Capital was exceptional. Their team's understanding of both markets ensured a smooth transaction and successful post-merger integration.",
      author: "Liu Wei",
      position: "Director, Eastern Pacific Investments"
    }
  ];

  return (
    <>
      <PageHeader
        title="International Markets"
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/traders-discussing-digital-currency-focus-real-time-market-graph-sellable_31965-465579.jpg_swhfj0.jpg"
      />

      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
                Global Financial Solutions
              </h2>
              <p className="text-lg text-gray-700">
                Segway Capital provides comprehensive international market expertise to help businesses and investors capitalize on global opportunities while effectively managing cross-border challenges.
              </p>
              <p className="text-lg text-gray-700">
                With offices in major financial hubs and a deep understanding of regional markets, we deliver strategic insights and practical solutions tailored to the unique complexities of international finance.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-secondary hover:bg-secondary-dark text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Discuss Global Opportunities
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/gradient-stock-market-concept_52683-76908.jpg_m8ry2v.jpg" 
                alt="International Markets" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Our International Market Services
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We offer specialized services designed to help clients navigate the complexities of global markets and seize cross-border opportunities.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {services.map((service, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{service.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3">{service.title}</h4>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Global Success Stories
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Learn how our international market expertise has helped clients achieve success across borders.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {clientTestimonials.map((testimonial, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md"
              >
                <div className="text-4xl text-primary opacity-30 mb-4">"</div>
                <p className="text-gray-700 italic mb-6">{testimonial.quote}</p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-primary">{testimonial.author}</p>
                  <p className="text-sm text-gray-500">{testimonial.position}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="bg-primary text-white rounded-xl p-10 shadow-lg">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl md:text-3xl font-heading font-bold mb-4">Ready to Expand Your Global Reach?</h3>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                Our international markets experts are ready to help you navigate global opportunities and challenges with confidence.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <Link 
                to="/contact-us" 
                className="px-8 py-3 bg-white text-primary hover:bg-gray-100 font-semibold rounded-lg transition-colors inline-block"
              >
                Request a Global Strategy Consultation
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default InternationalMarketsPage; 